import React from 'react';
import { Info } from 'lucide-react';
import { LayerDefinition, FieldType } from '@packages/database/types';
import { getDetailFields } from '@/helpers/layerSchema.helpers';
import { formatFieldValue } from '@/utils/map.utils';

export const AttributeRow = ({
  label,
  value,
  icon: Icon = Info,
}: {
  label: string;
  value: React.ReactNode;
  icon?: any;
}) => (
  <div className="flex items-start justify-between py-2.5 border-b border-border last:border-b-0">
    <div className="flex items-center gap-2.5 min-w-0 flex-1">
      <Icon size={14} className="text-foreground mt-0.5 flex-shrink-0" />
      <span className="text-xs font-medium text-foreground truncate">
        {label}
      </span>
    </div>
    <div className="text-xs font-semibold text-foreground ml-3 text-right">
      {value ?? <span className="text-muted-foreground">N/A</span>}
    </div>
  </div>
);

interface AssetDetailsContentProps {
  assetId: string;
  layerDef?: LayerDefinition | null;
  initialData?: Record<string, unknown>;
}

export const AssetDetailsContent = ({
  assetId,
  layerDef,
  initialData,
}: AssetDetailsContentProps) => {
  const data: Record<string, unknown> = {
    ...(initialData ?? {}),
  };

  const detailFields = layerDef ? getDetailFields(layerDef.schemaConfig) : null;

  if (!initialData) {
    return (
      <div className="space-y-2 p-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-8 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data)
    return (
      <div className="p-4 text-sm text-muted-foreground">No asset info</div>
    );

  return (
    <div className="p-4">
      {detailFields ? (
        <div>
          {detailFields.map((field) => {
            const value = data[field.alias];

            return (
              <AttributeRow
                key={field.column}
                label={field.fieldLabel ?? field.alias}
                value={formatFieldValue(
                  field.fieldType ?? FieldType.STRING,
                  value,
                )}
              />
            );
          })}

          {/* Custom attributes — dynamic key/value pairs outside schemaConfig */}
          {/* {data.customAttributes &&
              typeof data.customAttributes === 'object' &&
              Object.entries(
                data.customAttributes as Record<string, unknown>,
              ).map(([key, value]) => (
                <AttributeRow
                  key={key}
                  label={formatLabel(key)}
                  value={String(value ?? 'N/A')}
                />
              ))} */}
        </div>
      ) : (
        <div>
          {Object.entries(data)
            .filter(([, v]) => v !== null && typeof v !== 'object')
            .map(([key, value]) => (
              <AttributeRow
                key={key}
                label={formatLabel(key)}
                value={String(value)}
              />
            ))}
        </div>
      )}
    </div>
  );
};

// =========================================================================
// HELPERS
// =========================================================================

export const formatLabel = (key: string) => {
  if (!key) return '';
  const result = key.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};
